import React from "react";
import { EyeOff, Plus, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from "@/components/ui/dialog";
import { UserListPanel } from "@/components/UserListPanel";
import { BottomSheet } from "./StorySheets";

// Lot 652 — « Masquer ma story à » : liste au niveau du compte (users.story_hidden_from), appliquée à toutes mes stories.
export const useStoryHiddenCount = () => { const { user } = useApp(); return (user?.story_hidden_from || []).length; };

const useLabels = () => {
  const { t } = useApp();
  return {
    add: <><Plus size={12} /> {t("story.hidden_from_hide", "Masquer")}</>,
    remove: <><X size={12} /> {t("story.hidden_from_unhide", "Réafficher")}</>,
    listTitle: t("story.hidden_from_hidden_list", "Masqué pour"),
    empty: t("story.hidden_from_empty", "Personne n'est masqué pour l'instant."),
    added: t("story.hidden_from_added", "{name} ne verra plus tes stories"),
    removed: t("story.hidden_from_removed", "{name} peut de nouveau voir tes stories"),
  };
};

export const StoryHiddenFromPanel = ({ tone = "light" }) => <UserListPanel basePath="/users/me/story-hidden" testPrefix="story-hidden" labels={useLabels()} tone={tone} />;

// Petit lien « Masquer ma story à… · n » affiché sous le choix d'audience.
export const StoryHiddenFromLink = ({ onClick, tone = "light", testId = "story-hidden-link" }) => {
  const { t } = useApp();
  const count = useStoryHiddenCount();
  const dark = tone === "dark";
  return (
    <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 h-7 transition-colors ${dark ? "bg-white/10 text-white/85 hover:bg-white/15" : "hover:opacity-80"}`}
      style={dark ? undefined : { color: "var(--yalola-primary)", background: "var(--yalola-surface)" }} data-testid={testId} data-count={count}>
      <EyeOff size={13} /> {t("story.hidden_from_link", "Masquer ma story à…")}{count > 0 && <span className={`rounded-full px-1.5 text-[10px] ${dark ? "bg-white/20" : "text-white"}`} style={dark ? undefined : { background: "var(--yalola-primary)" }} data-testid={`${testId}-count`}>{count}</span>}
    </button>
  );
};

export const StoryHiddenFromDialog = ({ open, onOpenChange }) => {
  const { t } = useApp();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="sheet" className="sm:max-w-md z-[97]" overlayClassName="z-[96]" data-testid="story-hidden-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><EyeOff size={18} style={{ color: "var(--yalola-primary)" }} /> {t("story.hidden_from_title", "Masquer ma story à")}</DialogTitle>
          <DialogDescription>{t("story.hidden_from_desc", "Ces personnes ne verront aucune de tes stories, actuelles et futures, quelle que soit l'audience choisie. Elles ne sont pas prévenues.")}</DialogDescription>
        </DialogHeader>
        <DialogBody>{open && <StoryHiddenFromPanel tone="light" />}</DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export const StoryHiddenFromSheet = ({ open, onClose, t }) => (
  <BottomSheet open={open} onClose={onClose} title={t("story.hidden_from_title", "Masquer ma story à")} testId="story-fs-hidden-sheet" maxH="85dvh">
    <p className="text-xs text-white/65 mb-3">{t("story.hidden_from_desc", "Ces personnes ne verront aucune de tes stories, actuelles et futures, quelle que soit l'audience choisie. Elles ne sont pas prévenues.")}</p>
    <StoryHiddenFromPanel tone="dark" />
  </BottomSheet>
);

// Carte Réglages → Confidentialité.
export const StoryHiddenFromCard = () => {
  const { t } = useApp();
  return (
    <div className="yalola-card p-5" id="masquer-story" data-testid="settings-story-hidden-card">
      <div className="flex items-center gap-2 mb-1">
        <EyeOff size={18} style={{ color: "var(--yalola-primary)" }} />
        <div className="font-bold font-display text-lg">{t("story.hidden_from_title", "Masquer ma story à")}</div>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--yalola-text-muted)" }}>{t("story.hidden_from_desc", "Ces personnes ne verront aucune de tes stories, actuelles et futures, quelle que soit l'audience choisie. Elles ne sont pas prévenues.")}</p>
      <StoryHiddenFromPanel tone="light" />
    </div>
  );
};
